import { useCallback, useEffect, useState, type ReactNode } from "react";

import { money } from "../money.ts";
import type { PaymentOutcomeView, RiderApi } from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import {
  OutcomeShell,
  type Block,
  type BlockLine,
  type InstrumentValue,
  type OutcomeAction,
  type OutcomeKind,
} from "./OutcomeShell.tsx";
import { OutcomeState } from "./TrancheState.tsx";

/**
 * The five screens of `07 Payment Outcome Unknown.dc.html`, over EXP-002's `payment-outcomes` and
 * nothing else.
 *
 * One read, five screens, and **the read decides which**. That is the same shape as `3 Quote` /
 * `3b Quote expired` and `Cart` / `Cart conflict`, and here it is not a convenience: these screens
 * exist because a payment's outcome is unknown, and a screen chosen by the address rather than by
 * the answer could show a rider `Paid` over a payment nobody has confirmed. So the address is
 * corrected to whichever screen the answer names.
 *
 * | `outcome`      | screen                        |
 * | -------------- | ----------------------------- |
 * | `pending`      | `1 · Approve on phone`        |
 * | `unknown`      | `2 · Outcome unknown`, or `3 · Still unknown` past `STILL_UNKNOWN_MS` |
 * | `paid`         | `4a · Confirmed paid`         |
 * | `not_charged`  | `4b · Confirmed not charged`  |
 * | `unreadable`   | this application's error state — **not** one of the five |
 *
 * That last row is the distinction EXP-002 built the field for, in its own words: `unknown` is _"the
 * provider has not answered, or the two services disagree, which is what five designed screens are
 * about"_, and `unreadable` is _"this component failing to find out"_. Drawing `unreadable` as
 * `2 · Outcome unknown` would tell a rider that their provider is slow when in fact CityCab could
 * not read its own records, and those have different right answers.
 *
 * ## `Check again now` is a read, and that is the whole deliverable
 *
 * `CLAUDE.md` section 2: a "check status" action is a read, never a retry of the command. The design
 * says the same thing twice — _"'Check again now' is a read of the payment state, never a new
 * charge. It is safe to tap any number of times"_ and _"A retry button appears only after the
 * outcome is known to have failed (state 4b), when paying again is safe"_ — and EXP-002's
 * `payment-outcomes` is a `GET`. So the button calls the same `GET` this screen loaded with, under
 * no `Idempotency-Key`, and the one button that would be a command (`Try mobile money again`, on
 * `4b` only) cannot be sent at all: see `ACTIONS` below.
 *
 * ## What the five screens draw and cannot
 *
 * | Drawn                                       | From                                              |
 * | ------------------------------------------- | ------------------------------------------------- |
 * | `Paid` / `Not charged` / `Waiting…`         | EXP-002's `outcome`, from both services at once   |
 * | `KES 980`                                   | `amount`, withheld until PAY-01 confirms the payer |
 * | `Mobile money`                              | `method`, an enumeration of three                 |
 * | `··· 0421`                                  | **nothing** — `instrumentAbsence`                 |
 * | `pay_3M0E`                                  | `providerReference`                               |
 * | `You approved KES 980 at 21:14`             | `amount` and `requestedAt`                        |
 * | `Still waiting · 15 min`                    | `silentForMs`                                     |
 * | `Trip trp_8F2K`                             | `reference`, which names the trip or the order    |
 * | `Asked your provider 3 times · last at …`   | **nothing** — `attemptsAbsence`                   |
 * | `Get help` / `Chat with support`            | **nothing** — `supportAbsence`                    |
 * | `You'll get a notification…`                | **nothing** — RTM-001                             |
 * | `Est. KES 980 · Final KES 980`              | **nothing reachable** — see `receiptLines`        |
 */

export interface PaymentOutcomeProps {
  readonly api: RiderApi;
  readonly scopes: ReadonlySet<string>;
  readonly tenantId: string;
  readonly customerId: string;
  readonly executionId: string | undefined;
  /** Which of the five the address asked for. */
  readonly asked: PaymentPageId;
  /** Corrects the address when the answer names a different screen. */
  readonly onScreenChange: (pageId: string) => void;
  readonly onBack: () => void;
}

export type PaymentPageId =
  | "approve-on-phone"
  | "outcome-unknown"
  | "still-unknown"
  | "confirmed-paid"
  | "confirmed-not-charged";

/**
 * When `2 · Outcome unknown` becomes `3 · Still unknown`.
 *
 * Fifteen minutes, which is the design's own number: its third screen is titled
 * `3 · Still unknown after 15 min` in the reference screenshots and its chip reads
 * `Still waiting · 15 min`. The boundary is applied to EXP-002's `silentForMs`, which is published —
 * so unlike `Products · stock`'s freshness window this is a designed threshold against a published
 * measurement rather than a number this build had to invent.
 */
const STILL_UNKNOWN_MS = 15 * 60 * 1000;

export function PaymentOutcome(props: PaymentOutcomeProps): ReactNode {
  const { api, tenantId, customerId, executionId, asked, onScreenChange } = props;

  const [outcome, setOutcome] = useState<PaymentOutcomeView | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });
  /** The design's own `checking`: the label, the glyph and the line under the blocks all change. */
  const [checking, setChecking] = useState(false);

  const read = useCallback(
    (asCheck: boolean) => {
      if (executionId === undefined) return;
      if (asCheck) setChecking(true);
      else setState({ kind: "loading" });
      api
        .paymentOutcome({ tenantId, customerId, executionId })
        .then((answer) => {
          setOutcome(answer);
          setState({ kind: "ready" });
          setChecking(false);
        })
        .catch((error: unknown) => {
          setChecking(false);
          setState(stateForError(error, [SCOPES.journey]));
        });
    },
    [api, tenantId, customerId, executionId],
  );

  const load = useCallback(() => read(false), [read]);
  const check = useCallback(() => read(true), [read]);

  useEffect(load, [load]);

  const shouldBe = outcome === undefined ? undefined : pageFor(outcome);
  useEffect(() => {
    if (shouldBe !== undefined && shouldBe !== asked) onScreenChange(shouldBe);
  }, [shouldBe, asked, onScreenChange]);

  if (executionId === undefined) {
    return (
      <OutcomeState
        state={{
          kind: "error",
          code: "no_payment_in_address",
          message:
            "This screen is about one payment execution and the address does not name one. PAY-02 publishes no listing, so nothing can find it.",
          retryable: false,
          conflict: false,
        }}
        onBack={props.onBack}
        purpose="Reading what happened to your payment."
        barTitle="Payment"
        barSub="CityCab"
      />
    );
  }

  /*
   * `unreadable` is routed to the error state rather than to a screen. See this file's header.
   *
   * It is drawn as this application's own failure with the service's own word in it, because
   * "nobody has told us" and "we could not look" are different facts and only one of them is about
   * the rider's provider.
   */
  if (state.kind === "ready" && outcome?.outcome === "unreadable") {
    return (
      <OutcomeState
        state={{
          kind: "error",
          code: "payment_unreadable",
          message:
            "CityCab could not read this payment's records. This is not your provider being slow — it is us, and checking again is worth trying.",
          retryable: true,
          conflict: false,
        }}
        onRetry={load}
        onBack={props.onBack}
        purpose="Reading what happened to your payment."
        barTitle="Payment"
        barSub="CityCab"
      />
    );
  }

  if (state.kind !== "ready" || outcome === undefined) {
    return (
      <OutcomeState
        state={state}
        onRetry={load}
        onBack={props.onBack}
        purpose="Reading what happened to your payment."
        barTitle="Payment"
        barSub="CityCab"
      />
    );
  }

  const page = pageFor(outcome);
  const drawn = DRAWN[page];

  return (
    <OutcomeShell
      screen={page}
      onBack={props.onBack}
      {...subject(outcome)}
      kind={drawn.kind}
      heroGlyph={drawn.glyph}
      chip={drawn.chip(outcome)}
      title={drawn.title}
      sub={drawn.sub(outcome)}
      instrument={instrument(outcome)}
      {...(drawn.dont ? { dont: DONT } : {})}
      blocks={drawn.blocks(outcome)}
      {...checksLine(outcome, checking)}
      actions={drawn.actions({ outcome, checking, check, onBack: props.onBack })}
    />
  );
}

/** Which of the five the answer names. */
export function pageFor(outcome: PaymentOutcomeView): PaymentPageId {
  switch (outcome.outcome) {
    case "pending":
      return "approve-on-phone";
    case "paid":
      return "confirmed-paid";
    case "not_charged":
      return "confirmed-not-charged";
    case "unknown":
      return (outcome.silentForMs ?? 0) >= STILL_UNKNOWN_MS ? "still-unknown" : "outcome-unknown";
    case "unreadable":
      /*
       * Never drawn as a screen — the caller routes `unreadable` to the error state before this is
       * reached. Returned rather than thrown so the function stays total over the enumeration, which
       * is what makes a sixth value added to EXP-002's contract fail the build here.
       */
      return "outcome-unknown";
  }
}

/** The design's loudest element, drawn on the two uncertain screens and on no other. */
const DONT = "Don't pay again. You won't be charged twice for this trip.";

/* ------------------------------------------------------------------ the parts every screen shares */

/**
 * `Trip trp_8F2K · Westlands → Kilimani` — the app bar's second line.
 *
 * EXP-002 publishes `reference` as a type and an identifier, so the first half is drawable and the
 * second — the route — is not: nothing joins a payment to a trip's two place names, and R-28 records
 * that nothing joins a rider to a trip identifier either. So the line names what is published and
 * says what is missing beside it.
 */
function subject(
  outcome: PaymentOutcomeView,
): { readonly subject: string } | { readonly subjectAbsent: string } {
  if (outcome.reference === null) {
    return { subjectAbsent: "No subject: nothing says what this payment was for" };
  }
  return {
    subject: `${outcome.reference.type.replaceAll("_", " ")} ${outcome.reference.id}`,
  };
}

/**
 * The instrument card: the rail from `method`, the four digits from nothing.
 *
 * EXP-002's own sentence: _"the stored payment instrument the design draws as
 * `mobile money ··· 0421` — no service on this platform models one."_ So the row is drawn, the rail
 * is named, and the masked digits are the absence. `amountAbsence` has four values and each is a
 * different reason a figure is withheld — `payer_unconfirmed` and `not_this_payer` are a refusal to
 * publish somebody else's money, which is the right behaviour and is said as such.
 */
function instrument(outcome: PaymentOutcomeView): InstrumentValue {
  const rail =
    outcome.method === null
      ? undefined
      : { mobile_money: "Mobile money", card: "Card", bank: "Bank transfer" }[outcome.method];
  return {
    glyph:
      outcome.method === "card"
        ? "credit_card"
        : outcome.method === "bank"
          ? "account_balance"
          : "smartphone",
    ...(rail === undefined
      ? { nameAbsent: "No method: PAY-02 did not answer which rail this went by" }
      : { name: `${rail} · no stored instrument is published` }),
    ...(outcome.providerReference === null
      ? { referenceAbsent: "No provider reference" }
      : { reference: outcome.providerReference }),
    ...(outcome.amount === null
      ? { amountAbsent: `Withheld: ${outcome.amountAbsence ?? "payer_unconfirmed"}` }
      : { amount: money(outcome.amount) }),
  };
}

/**
 * `Asked your provider 3 times · last at 21:15:40`, which is always the absence.
 *
 * `attempts: null` with `attemptsAbsence: no_published_field` — the measurement names this element on
 * both uncertain screens, and the field is typed `null` in EXP-002's contract rather than nullable,
 * which is the component stating that there is nothing to publish and will not be until PAY-02
 * publishes an attempt count.
 *
 * While a check is in flight the design replaces the whole line with `Asking your provider now…`,
 * which **is** drawable: it is a fact about this application rather than about the provider.
 */
function checksLine(
  outcome: PaymentOutcomeView,
  checking: boolean,
): { readonly checks: string } | { readonly checksAbsent: string } | Record<string, never> {
  if (outcome.outcome !== "unknown") return {};
  if (checking) return { checks: "Asking your provider now…" };
  return {
    checksAbsent: `No attempt count: ${outcome.attemptsAbsence ?? "no_published_field"}`,
  };
}

/* ------------------------------------------------------------------------ the five, one each */

interface ActionContext {
  readonly outcome: PaymentOutcomeView;
  readonly checking: boolean;
  readonly check: () => void;
  readonly onBack: () => void;
}

interface DrawnScreen {
  readonly kind: OutcomeKind;
  readonly glyph: string;
  readonly chip: (outcome: PaymentOutcomeView) => string;
  readonly title: string;
  readonly sub: (outcome: PaymentOutcomeView) => string;
  readonly dont: boolean;
  readonly blocks: (outcome: PaymentOutcomeView) => readonly Block[];
  readonly actions: (context: ActionContext) => readonly OutcomeAction[];
}

/** `21:14` — the hour and minute of a published instant, on this device's clock. */
function clock(at: string | null): string | undefined {
  if (at === null) return undefined;
  return new Date(at).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** `You approved KES 980 at 21:14.` — the design's line, from two published fields. */
function approvedLine(outcome: PaymentOutcomeView): BlockLine {
  const at = clock(outcome.requestedAt);
  if (outcome.amount === null || at === undefined) {
    return {
      absent: "No approval on record: PAY-02 answered neither an amount nor a requested-at",
    };
  }
  return { text: `You approved ${money(outcome.amount)} at ${at}.` };
}

/**
 * `Your trip is complete. Nothing is on hold because of this.`
 *
 * EXP-002's `reference` names the trip and says nothing about its state, and nothing published joins
 * a payment to a trip's status. So the design's reassurance — which is a claim about the trip — is
 * the absence, on the three screens that make it.
 */
const TRIP_STATE: BlockLine = {
  absent: "No trip state: the payment names the trip it is for and nothing publishes how it ended",
};

/**
 * `You'll get a notification the moment we know.` — RTM-001, which the measurement names.
 *
 * Nothing delivers a payment outcome unasked to this application. The sentence is a promise, and a
 * promise this build cannot keep is drawn as the absence rather than printed.
 */
const NO_PUSH: BlockLine = {
  absent: "No notification: nothing delivers a payment outcome unasked to this app (RTM-001)",
};

/** `Get help` and `Chat with support`, both of which have no reachable route. */
const SUPPORT_UNREACHABLE =
  "SUP-01 publishes only /internal/v1 operations, which the gateway does not expose — so the designed way out of an unresolved payment is itself unreachable";

const DRAWN: Readonly<Record<PaymentPageId, DrawnScreen>> = {
  "approve-on-phone": {
    kind: "wait",
    glyph: "smartphone",
    chip: () => "Waiting for your approval",
    title: "Check your phone",
    sub: (outcome) =>
      outcome.amount === null
        ? "Approve the payment on the mobile money prompt. It can take a minute to arrive."
        : `Approve ${money(outcome.amount)} on the mobile money prompt. It can take a minute to arrive.`,
    dont: false,
    blocks: () => [
      {
        kind: "known",
        heading: "What's true now",
        lines: [
          /*
           * Drawable from the answer rather than asserted: `outcome: 'pending'` is taken from
           * PAY-02's execution **and** PAY-01's intent together, and neither of them says a capture
           * has been confirmed. That is exactly the sentence.
           */
          { text: "No money has been taken yet." },
          TRIP_STATE,
        ],
      },
    ],
    actions: () => [
      {
        label: "Waiting for the prompt…",
        glyph: "hourglass_top",
        primary: false,
        /*
         * Inert in the design too — its `onClick` is `()=>{}`. It is a status wearing a button's
         * shape, so it says so rather than appearing to be a control that does nothing.
         */
        unavailable: "This is a status, not a control: the prompt is on your phone",
      },
    ],
  },

  "outcome-unknown": {
    kind: "unknown",
    glyph: "help",
    chip: () => "Waiting for your provider",
    title: "We don't know yet if your payment went through",
    sub: (outcome) => {
      const at = clock(outcome.requestedAt);
      const approved = at === undefined ? "You approved it." : `You approved it at ${at}.`;
      /*
       * The second sentence is the uncertainty, and `uncertaintyReason` is the field the measurement
       * names as its source. Published, it is the service saying it has not been told, and the
       * design's sentence is drawn. Absent, nothing published says why — so the sentence is dropped
       * rather than asserted on this application's own authority.
       */
      return outcome.uncertaintyReason === null
        ? approved
        : `${approved} Your mobile money provider hasn't told us the result.`;
    },
    dont: true,
    blocks: (outcome) => [
      { kind: "known", heading: "What we know", lines: [approvedLine(outcome), TRIP_STATE] },
      {
        kind: "unknown",
        heading: "Not known yet",
        lines: [{ text: "Whether the money has left your account." }],
      },
      {
        kind: "next",
        heading: "What happens next",
        lines: [
          { text: "We keep asking your provider. You don't need to stay on this screen." },
          NO_PUSH,
          { text: "If the payment went through, it shows here as paid." },
        ],
      },
    ],
    actions: (context) => [
      checkAction(context, true),
      {
        label:
          context.outcome.providerReference === null
            ? "Get help"
            : `Get help · ref ${context.outcome.providerReference}`,
        glyph: "support_agent",
        primary: false,
        unavailable: SUPPORT_UNREACHABLE,
      },
    ],
  },

  "still-unknown": {
    kind: "unknown",
    glyph: "hourglass_bottom",
    chip: (outcome) => `Still waiting · ${silentFor(outcome)}`,
    title: "Your provider still hasn't answered",
    sub: (outcome) => {
      const at = clock(outcome.requestedAt);
      return at === undefined
        ? "This sometimes happens when their service is busy."
        : `This sometimes happens when their service is busy. Nothing has changed since ${at}.`;
    },
    dont: true,
    blocks: (outcome) => [
      {
        kind: "known",
        heading: "What we know",
        lines: [approvedLine(outcome), { text: "We have not confirmed any charge." }],
      },
      {
        kind: "unknown",
        heading: "Not known yet",
        lines: [{ text: "Whether the money has left your account." }],
      },
      {
        kind: "next",
        heading: "What happens next",
        lines: [
          {
            /*
             * `CityCab support has been told and is checking with the provider.` — the case raised
             * for the rider, which the measurement names. `support: null` with
             * `supportAbsence: no_published_route`, and the design's own notes say the same thing:
             * "Automatic hand-off to support after 15 minutes (state 3) is proposed; no endpoint
             * creates a SUP-01 case from a payment."
             */
            absent: "No case: nothing publishes a support case raised from a payment",
          },
          { text: "If you were charged, it shows here as paid. If not, it shows as not charged." },
        ],
      },
    ],
    actions: (context) => [
      {
        label: "Chat with support",
        glyph: "chat",
        primary: true,
        unavailable: SUPPORT_UNREACHABLE,
      },
      checkAction(context, false),
    ],
  },

  "confirmed-paid": {
    kind: "done",
    glyph: "check",
    chip: () => "Paid",
    title: "Payment confirmed",
    sub: (outcome) => {
      const at = clock(outcome.observedAt);
      return `Your provider confirmed it at ${at ?? "an instant PAY-02 did not publish"}. A receipt is in your trips.`;
    },
    dont: false,
    blocks: (outcome) => [{ kind: "known", heading: "Receipt", lines: receiptLines(outcome) }],
    actions: (context) => [
      /*
       * `Done` goes back, which is this build's reading of a control the design cycles.
       *
       * The design's own handler is `set({st:'prompt'})` — it returns to its first state, which is
       * the catalogue page cycling through its five frames rather than a product behaviour. Q-84
       * records the same shape of assumption for the app bar's back arrow.
       */
      { label: "Done", glyph: "check", primary: true, onPress: context.onBack },
      {
        label: "View receipt",
        glyph: "receipt_long",
        primary: false,
        unavailable:
          "Nothing publishes a receipt: PAY-02 answers an execution and no document, and there is no trip-receipt route",
      },
    ],
  },

  "confirmed-not-charged": {
    kind: "failed",
    glyph: "close",
    chip: () => "Not charged",
    title: "The payment didn't go through",
    sub: () => "Your provider confirmed nothing was taken. You can pay another way.",
    dont: false,
    blocks: (outcome) => [
      {
        kind: "known",
        heading: "What's true now",
        lines: [
          {
            /*
             * `You were not charged.` — the measurement names `providerCode` as its source: that the
             * refusal is the **provider's** answer and not this platform's inference. Published, the
             * sentence is the provider's. Absent, `outcome: not_charged` was taken from the two
             * states agreeing, which is still a fact but not the provider's word for it.
             */
            text:
              outcome.providerCode === null
                ? "You were not charged."
                : `You were not charged. Your provider said: ${outcome.providerCode}.`,
          },
          stillOwed(outcome),
        ],
      },
    ],
    actions: (outcomeContext) => [
      {
        label: "Try mobile money again",
        glyph: "smartphone",
        primary: true,
        /*
         * The one button on these five screens that is a command, drawn only here because paying
         * again is only safe once the outcome is known to have failed — and it cannot be sent.
         *
         * `POST /v1/payment-executions` (PAY-02) requires an `intentId`, and EXP-002's payment
         * outcome publishes the intent's **status** and not its identifier. So the retry the design
         * reserves for the one safe moment has no identifier to carry.
         */
        unavailable:
          "POST /v1/payment-executions (PAY-02) requires an intentId, and the payment outcome publishes the intent's status and not its id",
      },
      {
        label: "Pay by card",
        glyph: "credit_card",
        primary: false,
        unavailable:
          "Nothing publishes a stored payment instrument, so there is no card to choose — instrumentAbsence: " +
          (outcomeContext.outcome.instrumentAbsence ?? "no_published_service"),
      },
    ],
  },
};

/**
 * `Check again now`, with the design's own in-flight state.
 *
 * The label and the glyph both change while the read is in flight, which is the design's `act:
 * 'check'` branch. It is driven by the request rather than by a 1200ms timer, so the state lasts
 * exactly as long as the thing it is reporting.
 */
function checkAction(context: ActionContext, primary: boolean): OutcomeAction {
  return {
    label: context.checking ? "Checking…" : "Check again now",
    glyph: context.checking ? "progress_activity" : "refresh",
    primary,
    onPress: context.check,
  };
}

/** `Still waiting · 15 min`, from `silentForMs`. */
function silentFor(outcome: PaymentOutcomeView): string {
  if (outcome.silentForMs === null) return "for a while";
  const minutes = Math.max(1, Math.round(outcome.silentForMs / 60_000));
  return minutes < 60 ? `${String(minutes)} min` : `${String(Math.round(minutes / 60))} hr`;
}

/**
 * The receipt's two lines on `4a`, one drawable and one not.
 *
 * `KES 980 · mobile money ··· 0421` is the amount and the rail, with the four digits absent.
 * `Est. KES 980 · Final KES 980` is the estimate beside the final, and the estimate is **MOB-02's
 * quote**: this screen holds an execution identifier and a `reference` to a trip, and nothing joins
 * either to the quote the ride was priced at. So the second line is the absence.
 */
function receiptLines(outcome: PaymentOutcomeView): readonly BlockLine[] {
  const rail =
    outcome.method === null
      ? "the rail PAY-02 did not answer"
      : outcome.method.replaceAll("_", " ");
  return [
    outcome.amount === null
      ? { absent: `No amount: ${outcome.amountAbsence ?? "payer_unconfirmed"}` }
      : { text: `${money(outcome.amount)} · ${rail}` },
    {
      absent:
        "No estimate beside the final: nothing joins a payment to the MOB-02 quote the ride was priced at",
    },
  ];
}

/**
 * `You still owe KES 980 for this trip.` — the intent still outstanding.
 *
 * The measurement names PAY-01's `status` for this, and EXP-002 publishes it as `intentStatus`. An
 * intent that is `failed` or `canceled` is **not** still owed, and saying so would send a rider to
 * pay for something nobody is asking them for — so the sentence is drawn from the status rather than
 * from the screen's name.
 */
function stillOwed(outcome: PaymentOutcomeView): BlockLine {
  if (outcome.intentStatus === null) {
    return { absent: "No intent status: nothing says whether this is still owed" };
  }
  if (outcome.intentStatus === "canceled" || outcome.intentStatus === "succeeded") {
    return { text: "Nothing is outstanding on this trip." };
  }
  if (outcome.amount === null) {
    return { absent: `No amount outstanding: ${outcome.amountAbsence ?? "payer_unconfirmed"}` };
  }
  return { text: `You still owe ${money(outcome.amount)} for this trip.` };
}
